import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useExistingSections } from '../../hooks/useExistingSections';
import { useInvalidateNavLinks } from '../../hooks/useNavLinks';
import { usePermission } from '../../hooks/usePermission';
import { apiClient } from '../../services/api';
import { logger } from '../../services/logger';
import type { CreateSavedNavLinkDto, Customer, NavLinkScope, SavedNavLink, Team, User } from '../../types';

import { ICON_OPTIONS } from './bookmarkConstants';

export const BOOKMARK_ICON_OPTIONS = ICON_OPTIONS;

interface BookmarkViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPath: string;
  filterConfigJson?: string;
  columnConfigJson?: string;
  sortConfigJson?: string;
  existingBookmark?: SavedNavLink;
  existingSections?: string[];
}

export function BookmarkViewModal({
  isOpen,
  onClose,
  currentPath,
  filterConfigJson,
  columnConfigJson,
  sortConfigJson,
  existingBookmark,
  existingSections = [],
}: BookmarkViewModalProps) {
  const { user } = useAuth();
  const { addToast } = useToast();
  const invalidateNavLinks = useInvalidateNavLinks();
  const canManageTenant = usePermission('nav_links_tenant.manage');
  const canManageSystem = usePermission('nav_links_system.manage');
  const {
    sections: fetchedSections,
    isLoading: isSectionsLoading,
    isError: isSectionsError,
  } = useExistingSections(isOpen);

  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [sectionName, setSectionName] = useState('');
  const [scope, setScope] = useState<NavLinkScope>('User');
  const [canBeModifiedByOthers, setCanBeModifiedByOthers] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [sharingMode, setSharingMode] = useState<'everyone' | 'specific'>('everyone');
  const [sharedWithCustomerIds, setSharedWithCustomerIds] = useState<string[]>([]);
  const [sharedWithTeamIds, setSharedWithTeamIds] = useState<string[]>([]);
  const [sharedWithUserIds, setSharedWithUserIds] = useState<string[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingShareOptions, setIsLoadingShareOptions] = useState(false);

  const isEditMode = !!existingBookmark;
  const nameInputRef = useRef<HTMLInputElement>(null);

  const mergedSections = useMemo(() => {
    const canonicalByKey = new Map<string, string>();

    for (const section of [...existingSections, ...fetchedSections]) {
      const trimmed = section.trim();
      if (!trimmed) continue;
      const key = trimmed.toLowerCase();
      if (!canonicalByKey.has(key)) {
        canonicalByKey.set(key, trimmed);
      }
    }

    return Array.from(canonicalByKey.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
  }, [existingSections, fetchedSections]);

  const canonicalSectionMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const section of mergedSections) {
      map.set(section.trim().toLowerCase(), section);
    }
    return map;
  }, [mergedSections]);

  // Reset form state when modal opens
  useEffect(() => {
    if (!isOpen) return;

    setName(existingBookmark?.name ?? '');
    setIcon(existingBookmark?.icon ?? '');
    setSectionName(existingBookmark?.sectionName ?? '');
    setScope(existingBookmark?.scope ?? 'User');
    setCanBeModifiedByOthers(existingBookmark?.canBeModifiedByOthers ?? true);
    const hasSpecificSharing = (existingBookmark?.sharedWithCustomerIds?.length ?? 0) > 0
      || (existingBookmark?.sharedWithTeamIds?.length ?? 0) > 0
      || (existingBookmark?.sharedWithUserIds?.length ?? 0) > 0;
    setSharingMode(existingBookmark?.scope === 'Tenant' && hasSpecificSharing ? 'specific' : 'everyone');
    setSharedWithCustomerIds(existingBookmark?.sharedWithCustomerIds ?? []);
    setSharedWithTeamIds(existingBookmark?.sharedWithTeamIds ?? []);
    setSharedWithUserIds(existingBookmark?.sharedWithUserIds ?? []);
    setIsSaving(false);
    setShowIconPicker(false);

    const timeout = window.setTimeout(() => nameInputRef.current?.focus(), 0);
    return () => window.clearTimeout(timeout);
  }, [isOpen, existingBookmark]);

  useEffect(() => {
    if (!isOpen || scope !== 'Tenant' || !user?.tenantId) {
      return;
    }

    let isCancelled = false;
    setIsLoadingShareOptions(true);

    const loadShareOptions = async () => {
      try {
        const [loadedCustomers, loadedTeams, loadedUsers] = await Promise.all([
          apiClient.getCustomers(user.tenantId, { isActive: true }),
          apiClient.getTeams(user.tenantId),
          apiClient.getUsers(user.tenantId, true, false),
        ]);

        if (isCancelled) {
          return;
        }

        setCustomers(loadedCustomers.filter((customer) => customer.isActive));
        setTeams(loadedTeams.filter((team) => team.isActive));
        setUsers(loadedUsers.filter((loadedUser) => loadedUser.isActive !== false));
      } catch (error: unknown) {
        if (isCancelled) {
          return;
        }

        logger.error('Failed to load bookmark sharing options', { error: String(error) });
        addToast('Failed to load sharing options', 'error');
      } finally {
        if (!isCancelled) {
          setIsLoadingShareOptions(false);
        }
      }
    };

    void loadShareOptions();

    return () => {
      isCancelled = true;
    };
  }, [isOpen, scope, user?.tenantId, addToast]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || !isSectionsError) return;
    addToast('Some existing bookmark sections could not be loaded', 'error');
  }, [isOpen, isSectionsError, addToast]);

  const handleSave = async () => {
    if (!name.trim() || !user?.tenantId) return;
    const normalizedSectionInput = sectionName.trim();
    const canonicalSection = normalizedSectionInput
      ? canonicalSectionMap.get(normalizedSectionInput.toLowerCase())
      : undefined;
    const saveSection = canonicalSection ?? (normalizedSectionInput || undefined);

    // Capture all values before closing — component may unmount
    const saveScope = scope;
    const saveName = name.trim();
    const saveIcon = icon || undefined;
    const saveTenantId = user.tenantId;
    const saveUserId = user.userId;
    const saveCanBeModifiedByOthers = saveScope === 'System' ? false : canBeModifiedByOthers;
    const saveSharingMode = sharingMode;
    const saveSharedWithCustomerIds = [...sharedWithCustomerIds];
    const saveSharedWithTeamIds = [...sharedWithTeamIds];
    const saveSharedWithUserIds = [...sharedWithUserIds];
    const toast = addToast;
    const saveSharedCustomerIds = saveScope === 'Tenant'
      ? (saveSharingMode === 'specific' ? saveSharedWithCustomerIds : [])
      : undefined;
    const saveSharedTeamIds = saveScope === 'Tenant'
      ? (saveSharingMode === 'specific' ? saveSharedWithTeamIds : [])
      : undefined;
    const saveSharedUserIds = saveScope === 'Tenant'
      ? (saveSharingMode === 'specific' ? saveSharedWithUserIds : [])
      : undefined;

    // Close immediately for snappy UX — save in background
    onClose();

    try {
      if (isEditMode && existingBookmark) {
        await apiClient.updateNavLink(saveTenantId, existingBookmark.id, {
          id: existingBookmark.id,
          tenantId: saveScope !== 'System' ? saveTenantId : undefined,
          userId: saveScope === 'User' ? saveUserId : undefined,
          scope: saveScope,
          name: saveName,
          icon: saveIcon,
          sectionName: saveSection,
          targetPath: existingBookmark.targetPath,
          filterConfigJson: existingBookmark.filterConfigJson,
          columnConfigJson: existingBookmark.columnConfigJson,
          sortConfigJson: existingBookmark.sortConfigJson,
          sharedWithCustomerIds: saveSharedCustomerIds,
          sharedWithTeamIds: saveSharedTeamIds,
          sharedWithUserIds: saveSharedUserIds,
          canBeModifiedByOthers: saveCanBeModifiedByOthers,
        });
        toast('Bookmark updated', 'success');
      } else {
        const dto: CreateSavedNavLinkDto = {
          tenantId: saveScope !== 'System' ? saveTenantId : undefined,
          userId: saveScope === 'User' ? saveUserId : undefined,
          scope: saveScope,
          name: saveName,
          icon: saveIcon,
          sectionName: saveSection,
          targetPath: currentPath,
          filterConfigJson,
          columnConfigJson,
          sortConfigJson,
          sharedWithCustomerIds: saveSharedCustomerIds,
          sharedWithTeamIds: saveSharedTeamIds,
          sharedWithUserIds: saveSharedUserIds,
          canBeModifiedByOthers: saveCanBeModifiedByOthers,
        };
        await apiClient.createNavLink(saveTenantId, dto);
        toast('Bookmark saved', 'success');
      }
      invalidateNavLinks(saveScope);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      logger.error('Bookmark save failed', { scope: saveScope, name: saveName, status, detail, error: String(err) });
      toast(detail || `Failed to save bookmark (${status || 'unknown error'})`, 'error');
    }
  };

  if (!isOpen) return null;

  const toggleId = (
    id: string,
    setSelectedIds: (updater: (previous: string[]) => string[]) => void,
  ) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-label="Close bookmark modal"
      />

      <div className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {isEditMode ? 'Edit Bookmark' : 'Bookmark This View'}
        </h3>

        <div className="space-y-4">
          {/* Label */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Label
            </label>
            <input
              ref={nameInputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., My Open P1 Incidents"
              maxLength={100}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          {scope === 'Tenant' && (
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Sharing
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="bookmark-sharing-mode"
                    checked={sharingMode === 'everyone'}
                    onChange={() => setSharingMode('everyone')}
                  />
                  Everyone
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="bookmark-sharing-mode"
                    checked={sharingMode === 'specific'}
                    onChange={() => setSharingMode('specific')}
                  />
                  Specific customers, teams, users
                </label>
              </div>

              {sharingMode === 'specific' && (
                <div className="mt-3 space-y-3 rounded-lg border border-gray-200 p-3 dark:border-gray-600">
                  {isLoadingShareOptions && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">Loading options...</p>
                  )}

                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Customers
                    </p>
                    <div className="max-h-28 space-y-1 overflow-y-auto rounded border border-gray-200 p-2 dark:border-gray-700">
                      {customers.map((customer) => (
                        <label key={customer.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={sharedWithCustomerIds.includes(customer.id)}
                            onChange={() => toggleId(customer.id, setSharedWithCustomerIds)}
                          />
                          {customer.name}
                        </label>
                      ))}
                      {customers.length === 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">No customers available</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Teams
                    </p>
                    <div className="max-h-28 space-y-1 overflow-y-auto rounded border border-gray-200 p-2 dark:border-gray-700">
                      {teams.map((team) => (
                        <label key={team.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={sharedWithTeamIds.includes(team.id)}
                            onChange={() => toggleId(team.id, setSharedWithTeamIds)}
                          />
                          {team.name}
                        </label>
                      ))}
                      {teams.length === 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">No teams available</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Users
                    </p>
                    <div className="max-h-28 space-y-1 overflow-y-auto rounded border border-gray-200 p-2 dark:border-gray-700">
                      {users.map((listedUser) => (
                        <label key={listedUser.id} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={sharedWithUserIds.includes(listedUser.id)}
                            onChange={() => toggleId(listedUser.id, setSharedWithUserIds)}
                          />
                          {listedUser.fullName || listedUser.email}
                        </label>
                      ))}
                      {users.length === 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">No users available</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Icon Picker */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Icon
            </label>
            <button
              type="button"
              onClick={() => setShowIconPicker(!showIconPicker)}
              className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
            >
              <span className="text-lg">{icon || '📄'}</span>
              <span className="text-gray-500 dark:text-gray-400">
                {icon ? 'Change icon' : 'Choose icon'}
              </span>
            </button>
            {showIconPicker && (
              <div className="mt-2 grid grid-cols-10 gap-1 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-600 dark:bg-gray-700">
                {ICON_OPTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => {
                      setIcon(emoji);
                      setShowIconPicker(false);
                    }}
                    className={`flex h-8 w-8 items-center justify-center rounded text-lg transition-colors hover:bg-blue-100 dark:hover:bg-blue-900/30 ${
                      icon === emoji ? 'bg-blue-200 ring-2 ring-blue-500 dark:bg-blue-800' : ''
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Section Combobox */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Section
            </label>
            <Autocomplete
              freeSolo
              options={mergedSections}
              value={sectionName}
              onChange={(_, value) => setSectionName(typeof value === 'string' ? value : '')}
              onInputChange={(_, value) => setSectionName(value)}
              inputValue={sectionName}
              loading={isSectionsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type to search or create new..."
                  size="small"
                  slotProps={{
                    htmlInput: {
                      ...params.inputProps,
                      maxLength: 100,
                    },
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isSectionsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      borderRadius: '0.5rem',
                      fontSize: '0.875rem',
                    },
                  }}
                />
              )}
            />
          </div>

          {/* Scope */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Save to
            </label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <button
                type="button"
                onClick={() => setScope('User')}
                disabled={isEditMode && existingBookmark?.scope !== 'User'}
                className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                  scope === 'User'
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                ⭐ Favourites
              </button>
              {canManageTenant && (
                <button
                  type="button"
                  onClick={() => setScope('Tenant')}
                  disabled={isEditMode && existingBookmark?.scope !== 'Tenant'}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    scope === 'Tenant'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  🔗 Quick Links
                </button>
              )}
              {canManageSystem && (
                <button
                  type="button"
                  onClick={() => setScope('System')}
                  disabled={isEditMode && existingBookmark?.scope !== 'System'}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    scope === 'System'
                      ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                  }`}
                >
                  🌐 All
                </button>
              )}
            </div>
          </div>

          {scope !== 'System' && (
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={canBeModifiedByOthers}
                onChange={(e) => setCanBeModifiedByOthers(e.target.checked)}
              />
              Allow others to modify this view
            </label>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!name.trim() || isSaving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Bookmark' : 'Save Bookmark'}
          </button>
        </div>
      </div>
    </div>
  );
}
